import { FC } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { MobileCard } from "../../../domain/event.interface";

interface MobileCardSectionProps {
	mobileCard?: MobileCard;
	onChange: (mobileCard: MobileCard | undefined) => void;
}

export const MobileCardSection: FC<MobileCardSectionProps> = ({
	mobileCard,
	onChange,
}) => {
	const update = (patch: Partial<MobileCard>): void => {
		const next: MobileCard = { ...mobileCard, ...patch };
		const isEmpty = !next.title && !next.body && !next.imageUrl;
		onChange(isEmpty ? undefined : next);
	};

	return (
		<Grid item xs={12}>
			<Accordion>
				<AccordionSummary expandIcon={<ExpandMoreIcon />}>
					<Typography variant="subtitle2">Mobile Card</Typography>
				</AccordionSummary>
				<AccordionDetails>
					<Typography
						variant="caption"
						color="text.secondary"
						sx={{ display: "block", mb: 2 }}
					>
						Optional. Configures how this event renders in the mobile day-event
						list. If left empty, the mobile app uses default rendering.
					</Typography>
					<Grid container spacing={2}>
						<Grid item xs={12} sm={6}>
							<TextField
								fullWidth
								size="small"
								label="Card Title"
								value={mobileCard?.title || ""}
								onChange={(e) => update({ title: e.target.value || undefined })}
								inputProps={{ maxLength: 60 }}
								helperText="Max 60 characters"
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								fullWidth
								size="small"
								label="Card Body"
								value={mobileCard?.body || ""}
								onChange={(e) => update({ body: e.target.value || undefined })}
								inputProps={{ maxLength: 200 }}
								helperText="Max 200 characters"
								multiline
								rows={2}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								fullWidth
								size="small"
								label="Image URL"
								value={mobileCard?.imageUrl || ""}
								onChange={(e) =>
									update({ imageUrl: e.target.value || undefined })
								}
								helperText="Hosted image URL displayed on the card"
								placeholder="https://..."
							/>
						</Grid>
						{mobileCard?.imageUrl && (
							<Grid item xs={12}>
								<Box
									sx={{
										border: 1,
										borderColor: "divider",
										borderRadius: 1,
										p: 1,
										display: "inline-block",
									}}
								>
									<Box
										component="img"
										src={mobileCard.imageUrl}
										alt="Mobile card preview"
										sx={{
											maxHeight: 120,
											maxWidth: 240,
											display: "block",
										}}
										onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
											(e.currentTarget as HTMLImageElement).style.display =
												"none";
										}}
									/>
								</Box>
							</Grid>
						)}
					</Grid>
				</AccordionDetails>
			</Accordion>
		</Grid>
	);
};
